import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  FormControl,
  FormLabel,
  Typography,
  Grid,
} from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import { NYHalfWidthNumberToFillWidthNumber } from "@/utils/utils";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import {
  Button,
  ButtonVariantOption,
  ButtonColorOption,
} from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { type ChibanInput } from "@/types/kaokuNumber";
import type { BookTypeEng } from "@/types/acquirebook";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import { formatArea } from "../utils";
import { useMap } from "react-leaflet";
import type { PopupContentProps } from "../types/popup";
import { addMapClickTagUpdateListener } from "../utils/tagSync/mapClickTagSync";
import { addMapClickRemarksUpdateListener } from "../utils/tagSync/mapClickRemarksSync";
import { useFeatureFlags } from "@/configs/featureFlag";
import { addMapClickAdditionalFieldsUpdateListener } from "../utils/tagSync/mapClickAdditionalFieldsSync";
import { MemoModal } from "./MemoModal";
import { OtherButtonModal } from "./OtherButtonModal";
import { getGoogleMapsUrl } from "../utils/openGoogleMaps";
import { getChikaMapUrl } from "../utils/openChikaMap";
import { AddressIntegrator } from "@/utils/AddressIntegrator";
import { useSaveInitialMapPosition } from "@/features/map/hooks/useSaveInitialMapPosition";
import { toast } from "react-toastify";

/**
 * 地図上に表示するポップアップ画面の内容を表すReactコンポーネント
 * @param props addressChiban 地番として表示する文字列
 * @param props address 住居表示として表示する文字列
 * @param props prefName 登記取得画面に渡す文字列
 * @param props cityName 登記取得画面に渡す文字列
 * @param props ooazaName 登記取得画面に渡す文字列
 * @param props azaName 登記取得画面に渡す文字列
 * @param props chiban 登記取得画面に渡す文字列
 * @param props area 推計地積表示のために渡す数値
 */
export const PopupContent: React.FC<PopupContentProps> = (props) => {
  const {
    addressChiban,
    address,
    prefName,
    cityName,
    ooazaName,
    azaName,
    chiban,
    handleKaokuNumberSearch,
    handleRealEstateDocumentFetchModal,
    handleNewPropertySheet,
    kaokuNumberSearchFlag = false,
    realEstateDocumentFetchFlag = false,
    area,
    handlePinSave,
    initialTags = [],
    onTagsChange,
    initialRemarks = "",
    onRemarksChange,
    initialAdditionalFields = "",
    onAdditionalFieldsChange,
    crm = false,
    hasMapViewingPermission = false,
    latlng,
    availableFields,
    hasAvailableFields = false,
    sourceLabel,
  }: PopupContentProps = props;

  const { propertySummary, priceMap } = useFeatureFlags();
  const { saveInitialMapPosition, isSavingInitialMapPosition } =
    useSaveInitialMapPosition();

  // 地図を表示しているコンテナサイズを取得するために、Mapオブジェクトを取得する
  // 正しくMapオブジェクトを取得するためには、このReactコンポーネント<PopupContent>が<MapContainer>の子要素である必要があります
  const map = useMap();

  // メモモーダル（タグ・備考・カスタム項目編集）の表示状態
  const [memoModalOpen, setMemoModalOpen] = useState(false);

  // その他操作モーダルの表示状態
  const [otherButtonModalOpen, setOtherButtonModalOpen] = useState(false);

  // タグ・備考・カスタム項目の現在値を管理（モーダルに渡すため）
  const [currentTags, setCurrentTags] = useState(initialTags);
  const [currentRemarks, setCurrentRemarks] = useState(initialRemarks);
  const [currentAdditionalFields, setCurrentAdditionalFields] = useState(
    initialAdditionalFields
  );

  // 親コンポーネントから渡される初期値が変更された場合、内部状態を同期
  useEffect(() => {
    setCurrentTags(initialTags);
    setCurrentRemarks(initialRemarks);
    setCurrentAdditionalFields(initialAdditionalFields);
  }, [initialTags, initialRemarks, initialAdditionalFields]);

  // 地図クリックポップアップのタグ更新イベントリスナー
  useEffect(() => {
    const cleanup = addMapClickTagUpdateListener((newTags) => {
      setCurrentTags(newTags);
    });

    return cleanup;
  }, []);

  // 地図クリックポップアップの備考更新イベントリスナー
  useEffect(() => {
    const cleanup = addMapClickRemarksUpdateListener((newRemarks) => {
      setCurrentRemarks(newRemarks);
    });

    return cleanup;
  }, []);

  // 地図クリックポップアップの追加項目更新イベントリスナー
  useEffect(() => {
    const cleanup = addMapClickAdditionalFieldsUpdateListener(
      (newAdditionalFields) => {
        setCurrentAdditionalFields(newAdditionalFields);
      }
    );

    return cleanup;
  }, []);

  const buttonState: boolean =
    prefName !== "" &&
    cityName !== "" &&
    /*
    大字、字については存在しない場合もあるので空文字でもボタンは有効にする
    ooazaName !== "" &&
    azaName !== "" &&
    */
    chiban !== "";

  const buildLocationString = (
    cityName: string,
    ooazaName: string | null,
    azaName: string | null
  ): string => {
    return `${cityName}${ooazaName ?? ""}${azaName ?? ""}`;
  };

  const handleSetInitialPosition =
    React.useCallback(async (): Promise<void> => {
      if (!latlng) {
        toast.error("座標が取得できませんでした");
        return;
      }
      await saveInitialMapPosition(latlng.lat, latlng.lng);
    }, [latlng, saveInitialMapPosition]);

  const canOpenOtherButton =
    hasMapViewingPermission || !!handleSetInitialPosition;

  const handleClickKaokuNumberSearchButton = (): void => {
    if (handleKaokuNumberSearch) {
      const c: ChibanInput = {
        prefectureName: prefName,
        location: buildLocationString(cityName, ooazaName, azaName),
        chiban,
        bookType: "LAND" as BookTypeEng,
        latitude: latlng?.lat,
        longitude: latlng?.lng,
      };
      handleKaokuNumberSearch([c]);
    }
  };

  // Google Maps（衛星写真）のURL
  const googleMapsUrl = getGoogleMapsUrl(map, latlng);

  // 地価マップのURL
  const chikaMapUrl = getChikaMapUrl(latlng);

  // 地図コンテナの横幅
  const mapContainerSizeX = map.getSize().x;

  // ポップアップのデフォルトサイズ(px)
  let popupWidth = 300;

  // 地図コンテナ幅375px以下では（300px幅のポップアップ＋余白）がきれいに収まらないので、ポップアップ幅を縮小する
  if (mapContainerSizeX < 375) {
    popupWidth = Math.floor(mapContainerSizeX * 0.8);
  }

  return (
    // ここでポップアップ幅を設定しているが、これとは別にleafletのpopupオブジェクト側でもポップアップ幅の制御が行われているので
    // そちらも確認する必要があります
    <>
      <Box sx={{ width: popupWidth }}>
        <FormControl>
          <FormLabel>
            <PlaceIcon fontSize={"inherit"} />
            住居表示
          </FormLabel>
          <Typography
            ml={2}
            my={1}
            fontSize={"0.8rem"}
            variant={"body1"}
            component={"span"}
          >
            {NYHalfWidthNumberToFillWidthNumber(address)}
          </Typography>
          <FormLabel>
            <ImportContactsIcon fontSize={"inherit"} /> 地番
          </FormLabel>
          <Typography
            ml={2}
            my={1}
            fontSize={"0.8rem"}
            variant={"body1"}
            component={"span"}
          >
            {NYHalfWidthNumberToFillWidthNumber(addressChiban)}
          </Typography>
          {sourceLabel && (
            <Chip
              label={sourceLabel}
              size={"small"}
              variant={"outlined"}
              sx={{
                ml: 2,
                mb: 1,
                height: 20,
                alignSelf: "flex-start",
                maxWidth: "100%",
                "& .MuiChip-label": { fontSize: "0.65rem", px: 1 },
              }}
            />
          )}
          <FormLabel>
            <SquareFootIcon fontSize={"inherit"} />
            推計地積(㎡)
          </FormLabel>
          <Typography
            ml={2}
            my={1}
            fontSize={"0.8rem"}
            variant={"body1"}
            component={"span"}
          >
            {formatArea(area)}
          </Typography>
        </FormControl>
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button
                label={"登記を取得"}
                disabled={!buttonState}
                onClick={() => {
                  let url = "/acquirebook";
                  if (prefName && cityName && location && chiban) {
                    url =
                      url +
                      "?" +
                      `prefecture=${prefName}` +
                      `&city=${cityName}` +
                      `&location=${NYHalfWidthNumberToFillWidthNumber(
                        ooazaName
                      )}${NYHalfWidthNumberToFillWidthNumber(azaName)}` +
                      `&chiban=${NYHalfWidthNumberToFillWidthNumber(chiban)}` +
                      `&realEstate=LAND`;
                  }
                  window.open(url, "_blank");
                }}
                size={SizingWrapperStyle.INHERIT}
                variant={ButtonVariantOption.Contained}
                color={ButtonColorOption.Primary}
              />
            </Grid>
            <Grid item xs={crm ? 6 : 12}>
              <Button
                label={"その他操作"}
                disabled={!canOpenOtherButton}
                onClick={() => {
                  setOtherButtonModalOpen(true);
                }}
                size={SizingWrapperStyle.INHERIT}
                variant={ButtonVariantOption.Outlined}
                color={ButtonColorOption.Primary}
              />
            </Grid>
            {crm && (
              <Grid item xs={6}>
                <Button
                  label={"メモ"}
                  disabled={!hasMapViewingPermission}
                  onClick={() => {
                    setMemoModalOpen(true);
                  }}
                  size={SizingWrapperStyle.INHERIT}
                  variant={ButtonVariantOption.Outlined}
                  color={ButtonColorOption.Primary}
                  startIcon={<NoteAltIcon />}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>
      <MemoModal
        open={memoModalOpen}
        onClose={() => {
          setMemoModalOpen(false);
        }}
        initialTags={currentTags}
        initialRemarks={currentRemarks}
        initialAdditionalFields={currentAdditionalFields}
        onTagsChange={(newTags) => {
          setCurrentTags(newTags);
          onTagsChange?.(newTags);
        }}
        onRemarksChange={(newRemarks) => {
          setCurrentRemarks(newRemarks);
          onRemarksChange?.(newRemarks);
        }}
        onAdditionalFieldsChange={(newAdditionalFields) => {
          setCurrentAdditionalFields(newAdditionalFields);
          onAdditionalFieldsChange?.(newAdditionalFields);
        }}
        availableFields={availableFields}
        hasAvailableFields={hasAvailableFields}
      />
      <OtherButtonModal
        open={otherButtonModalOpen}
        onClose={() => {
          setOtherButtonModalOpen(false);
        }}
        kaokuNumberSearchFlag={kaokuNumberSearchFlag}
        handleKaokuNumberSearch={
          buttonState ? handleClickKaokuNumberSearchButton : undefined
        }
        realEstateDocumentFetchFlag={realEstateDocumentFetchFlag}
        handleRealEstateDocumentFetchModal={
          handleRealEstateDocumentFetchModal
            ? () => {
                handleRealEstateDocumentFetchModal(
                  prefName,
                  address,
                  chiban,
                  buildLocationString(cityName, ooazaName, azaName),
                  true
                );
              }
            : undefined
        }
        handlePinSave={handlePinSave}
        handleNewPropertySheet={
          handleNewPropertySheet && buttonState
            ? () => {
                const adi = new AddressIntegrator({
                  addressChiban,
                  address,
                  prefName,
                  cityName,
                  ooazaName,
                  azaName,
                  chiban,
                  area,
                  latlng,
                });
                handleNewPropertySheet(adi);
              }
            : undefined
        }
        propertySummary={!!propertySummary}
        handleSetInitialPosition={handleSetInitialPosition}
        isSavingInitialPosition={isSavingInitialMapPosition}
        googleMapsUrl={googleMapsUrl}
        chikaMapUrl={priceMap ? chikaMapUrl : undefined}
      />
    </>
  );
};
